/**
 * Where to write a nation's name on the map: a good interior point of its largest landmass — the
 * hex farthest (in hex steps) from any border or coast (a pole of inaccessibility on the hex grid),
 * ties to the one nearest the landmass's centre, so the name sits inland, never in the sea the way
 * a centroid of a curved coast would. Borders are public, so the view's owners are fair to use.
 *
 * Stable as borders move: last time's point is kept while it is still that nation's, on the same
 * landmass and within one step of the deepest — a few hexes changing hands never makes the label
 * jump. Pure (tested in test/nations.test.ts).
 */
import type { HexIdx, PlayerId, WorldMap } from '@sa/sim';

export interface NationAnchor {
  player: PlayerId;
  hex: HexIdx;
  x: number;
  y: number;
  /** hex steps from the anchor to the nearest border or coast (1: on the edge) */
  depth: number;
  /** hexes of that landmass */
  area: number;
}

export interface NationPlaces {
  anchors: Map<PlayerId, NationAnchor>;
  /** per land hex: steps to the nearest border / coast of its owner's land (0: neutral) */
  depth: Int16Array;
}

/** The anchors of every nation that holds land (see the file comment); `prev` keeps them steady. */
export function nationPlaces(world: Pick<WorldMap, 'landCount' | 'nbr' | 'cx' | 'cy'>, owner: ArrayLike<number>, prev?: ReadonlyMap<PlayerId, NationAnchor>): NationPlaces {
  const n = world.landCount;
  const nbr = world.nbr;
  const depth = new Int16Array(n);
  // multi-source BFS from every hex on an edge (a neighbour of another owner, or the sea)
  const queue = new Int32Array(n);
  let qh = 0;
  let qt = 0;
  for (let h = 0; h < n; h++) {
    const o = owner[h];
    if (o === 0) continue;
    for (let k = 0; k < 6; k++) {
      const m = nbr[h * 6 + k];
      if (m < 0 || owner[m] !== o) {
        depth[h] = 1;
        queue[qt++] = h;
        break;
      }
    }
  }
  while (qh < qt) {
    const h = queue[qh++];
    const o = owner[h];
    for (let k = 0; k < 6; k++) {
      const m = nbr[h * 6 + k];
      if (m >= 0 && owner[m] === o && depth[m] === 0) {
        depth[m] = depth[h] + 1;
        queue[qt++] = m;
      }
    }
  }
  // landmasses: connected same-owner hexes; each nation's largest
  const comp = new Int32Array(n).fill(-1);
  const best = new Map<PlayerId, { id: number; size: number; sx: number; sy: number; start: number }>();
  let id = 0;
  for (let s = 0; s < n; s++) {
    const o = owner[s];
    if (o === 0 || comp[s] >= 0) continue;
    let size = 0;
    let sx = 0;
    let sy = 0;
    qh = qt = 0;
    queue[qt++] = s;
    comp[s] = id;
    while (qh < qt) {
      const h = queue[qh++];
      size++;
      sx += world.cx[h];
      sy += world.cy[h];
      for (let k = 0; k < 6; k++) {
        const m = nbr[h * 6 + k];
        if (m >= 0 && owner[m] === o && comp[m] < 0) {
          comp[m] = id;
          queue[qt++] = m;
        }
      }
    }
    const b = best.get(o);
    if (!b || size > b.size) best.set(o, { id, size, sx, sy, start: s });
    id++;
  }
  const anchors = new Map<PlayerId, NationAnchor>();
  for (const [o, b] of best) {
    const mx = b.sx / b.size;
    const my = b.sy / b.size;
    let pick = -1;
    let pd = 0;
    let pc = Infinity;
    for (let h = b.start; h < n; h++) {
      if (comp[h] !== b.id) continue;
      const d = depth[h];
      const c = (world.cx[h] - mx) ** 2 + (world.cy[h] - my) ** 2;
      if (d > pd || (d === pd && c < pc)) {
        pick = h;
        pd = d;
        pc = c;
      }
    }
    // hysteresis: last time's point while it is still good enough
    const was = prev?.get(o);
    if (was && was.hex < n && owner[was.hex] === o && comp[was.hex] === b.id && depth[was.hex] >= pd - 1) pick = was.hex;
    anchors.set(o, { player: o, hex: pick, x: world.cx[pick], y: world.cy[pick], depth: depth[pick], area: b.size });
  }
  return { anchors, depth };
}
